import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { isRTL } from '../i18n/i18n';

/**
 * Phase 1 of Hebrew/RTL: make MUI + Emotion follow the active language.
 *
 * Two things have to switch together for MUI to mirror correctly:
 *   1. an Emotion cache whose stylis pipeline includes `rtlPlugin`, which flips
 *      physical left/right in the CSS MUI (and any `sx`/`styled`) emits, and
 *   2. a MUI theme with `direction: 'rtl'`, which drives MUI's own logical
 *      component styles.
 *
 * The direction is read from the i18n language via `isRTL()` - the SAME signal
 * Phase 0 writes onto <html dir>. Tailwind-driven layout already follows that
 * attribute; this closes the loop for the ~44 MUI-using surfaces.
 *
 * Caches are created once at module scope (creating one per render leaks style
 * tags and re-inserts on every paint). Only `direction` is set on the theme, so
 * nothing about the existing MUI defaults or the Tailwind dark-mode theming
 * changes - in LTR this is a no-op.
 */
const ltrCache = createCache({ key: 'mui', stylisPlugins: [prefixer] });
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });

interface RtlProviderProps {
  children: React.ReactNode;
}

export const RtlProvider: React.FC<RtlProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const rtl = isRTL(i18n.language);

  const theme = useMemo(() => createTheme({ direction: rtl ? 'rtl' : 'ltr' }), [rtl]);

  return (
    <CacheProvider value={rtl ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </CacheProvider>
  );
};

export default RtlProvider;
